<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getInboxSource } from 'dashboard/helper/inbox';
import { serverErrorMessage } from 'dashboard/store/utils/api';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import InboxPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/InboxPageEmptyState.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const assistantId = computed(() => Number(route.params.assistantId));
const inboxes = useMapGetter('inboxes/getInboxes');
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');
const agentBots = useMapGetter('agentBots/getBots');
const connectedInboxes = useMapGetter('captainInboxes/getRecords');
const uiFlags = useMapGetter('captainInboxes/getUIFlags');
const assistants = useMapGetter('captainAssistants/getRecords');

const saving = ref({});

const assistantName = computed(
  () => assistants.value.find(item => item.id === assistantId.value)?.name
);

const connectedIds = computed(
  () => new Set(connectedInboxes.value.map(inbox => inbox.id))
);

// One inbox holds one bot, of any kind. An inbox answered by another bot is listed with its name
// instead of hidden: freeing it is a decision somebody has to make on that bot.
const botByInbox = computed(() => {
  const map = {};
  agentBots.value.forEach(bot => {
    (bot.inboxes || []).forEach(inbox => {
      map[inbox.id] = bot;
    });
  });
  return map;
});

const rows = computed(() => {
  // Answering first, then the ones it can take, then the ones another bot holds.
  const order = row => {
    if (row.isOn) return 0;
    return row.takenBy ? 2 : 1;
  };
  return inboxes.value
    .map(inbox => {
      const isOn = connectedIds.value.has(inbox.id);
      const owner = botByInbox.value[inbox.id];
      return {
        inbox,
        isOn,
        takenBy: !isOn && owner ? owner : null,
        source: getInboxSource(inbox.channel_type, inbox.phone_number, inbox),
      };
    })
    .sort((a, b) => order(a) - order(b));
});

// Counted over the inboxes listed: a deleted inbox can still hold the bot, and is not one of them.
const answeringCount = computed(
  () => rows.value.filter(row => row.isOn).length
);

const isFetching = computed(
  () =>
    (uiFlags.value.fetchingList && !connectedInboxes.value.length) ||
    (inboxUiFlags.value.isFetching && !inboxes.value.length)
);

const refresh = () =>
  Promise.all([
    store.dispatch('captainInboxes/get', { assistantId: assistantId.value }),
    // Only to name the bot holding an inbox; an agent without access to bots still sees the list.
    store.dispatch('agentBots/get').catch(() => {}),
  ]);

const toggle = async row => {
  const { inbox } = row;
  saving.value[inbox.id] = true;
  try {
    if (row.isOn) {
      await store.dispatch('captainInboxes/delete', {
        assistantId: assistantId.value,
        inboxId: inbox.id,
      });
    } else {
      await store.dispatch('captainInboxes/create', {
        assistantId: assistantId.value,
        inboxId: inbox.id,
      });
    }
    useAlert(
      t(row.isOn ? 'CAPTAIN.INBOXES.TOGGLE.OFF' : 'CAPTAIN.INBOXES.TOGGLE.ON', {
        agent: assistantName.value,
        inbox: inbox.name,
      })
    );
  } catch (error) {
    useAlert(serverErrorMessage(error) || t('CAPTAIN.INBOXES.TOGGLE.ERROR'));
  } finally {
    await refresh();
    saving.value[inbox.id] = false;
  }
};

const openBot = bot =>
  router.push({
    name: 'agent_bot_show',
    params: { accountId: route.params.accountId, botId: bot.id },
  });

const createInbox = () =>
  router.push({
    name: 'settings_inbox_new',
    params: { accountId: route.params.accountId },
  });

store.dispatch('inboxes/get');
watch(assistantId, refresh, { immediate: true });
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.INBOXES.HEADER')"
    :is-fetching="isFetching"
    :is-empty="!inboxes.length"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
  >
    <template #emptyState>
      <InboxPageEmptyState @click="createInbox" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <p class="text-sm text-n-slate-11">
            {{ $t('CAPTAIN.INBOXES.DESCRIPTION') }}
          </p>
          <p class="text-sm font-medium text-n-slate-12">
            {{
              $t('CAPTAIN.INBOXES.SUMMARY', {
                count: answeringCount,
                total: rows.length,
              })
            }}
          </p>
        </div>

        <ul
          class="flex flex-col overflow-hidden divide-y rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak divide-n-weak"
        >
          <li
            v-for="row in rows"
            :key="row.inbox.id"
            class="flex items-center gap-4 px-4 py-3"
          >
            <div
              class="flex items-center justify-center rounded-lg size-9 shrink-0 bg-n-alpha-2"
            >
              <ChannelIcon :inbox="row.inbox" class="size-4 text-n-slate-11" />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ row.inbox.name }}
              </span>
              <span v-if="row.takenBy" class="text-xs text-n-amber-11">
                {{ $t('CAPTAIN.INBOXES.TAKEN_BY') }}
                <button
                  type="button"
                  class="underline reset-base hover:text-n-amber-12"
                  @click="openBot(row.takenBy)"
                >
                  {{ row.takenBy.name }}
                </button>
              </span>
              <span
                v-else-if="row.source"
                class="text-xs truncate text-n-slate-11"
              >
                {{ row.source }}
              </span>
            </div>
            <span
              v-if="row.isOn"
              class="flex items-center gap-1 text-xs shrink-0 text-n-teal-11"
            >
              <span class="rounded-full size-1.5 bg-n-teal-9" />
              {{ $t('CAPTAIN.INBOXES.ANSWERING') }}
            </span>
            <Switch
              :model-value="row.isOn"
              :disabled="
                !isAdmin || Boolean(row.takenBy) || saving[row.inbox.id]
              "
              @change="toggle(row)"
            />
          </li>
        </ul>
      </div>
    </template>
  </PageLayout>
</template>
